/**
 * useStoryCards hook.
 *
 * Subscribes to the 'alerts' WebSocket topic and manages the story card stack.
 * Cards are created on approach/entry alerts and resolved on turnaround/entry
 * followup alerts matching the same fingerprint within 120 seconds.
 *
 * Resolution heuristic (Phase 3 - no formal alert.resolved frame from server):
 *   A followup alert frame with the SAME fingerprint within 120s of an existing
 *   'detecting' card is treated as a resolution event. The alert_type determines
 *   the resolution outcome:
 *     - 'turnaround' -> state='turned-away' (auto-dismiss after 30s)
 *     - 'entry'      -> state='entered' (manual dismiss only)
 *     - 'loiter_resolved' -> state='ambiguous' (auto-dismiss after 30s)
 *
 * Known risk: if the detector emits turnaround/entry before this plan's WS
 * frame normalization is active, the matching may miss. Plan 03-02 normalizes
 * all alert_type values to canonical strings before fan-out, so the client
 * heuristic is deterministic as long as 03-02 is deployed.
 *
 * Soft cap at 50 total cards: when exceeded, oldest resolved cards are dropped
 * to prevent unbounded state growth.
 *
 * Time zone note: "Entered at HH:MM" uses browser local time in Phase 3.
 * Phase 5 adds site.timezone and passes it here via context.
 *
 * Design spec reference: section 4, 4.1, 4.2.
 * Plan 03-06, CL-04, API-09.
 */
import { useState, useEffect, useCallback } from 'react';
import { useWebSocket } from '@/features/realtime/useWebSocket';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** State of a single story card in the stack. */
export interface StoryCardState {
  /** Alert primary key from fleet-api. */
  alertId: number;
  /** Epoch ms when the approach alert was received. */
  openedAt: number;
  /** Device fingerprint for matching resolution events. */
  fingerprint: string | null;
  /** Zone name for the card header (e.g. "Threshold"). */
  zoneName: string | null;
  /** Alert severity for header color tint. */
  severity: 'info' | 'warning' | 'critical';
  /** Original alert_type of the opening alert. */
  alertType: string;
  /** Camera evidence URL if provided by the server; null otherwise. */
  cameraUrl: string | null;
  /** Current lifecycle state of the card. */
  state: 'detecting' | 'turned-away' | 'entered' | 'ambiguous';
  /** Epoch ms when the card was resolved (null while detecting). */
  resolvedAt: number | null;
  /** Human-readable resolution text (null while detecting). */
  resolutionText: string | null;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Window in which a followup alert is considered a resolution for the same device. */
const RESOLUTION_MATCH_WINDOW_MS = 120_000;

/** Auto-dismiss delay for turned-away and ambiguous cards (ms). */
const AUTO_DISMISS_DELAY_MS = 30_000;

/** Soft cap on total story cards to prevent unbounded growth. */
const MAX_TOTAL_CARDS = 50;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Formats a timestamp epoch ms into a short "HH:MM" local time string.
 * Phase 5 will pass site.timezone and use Intl.DateTimeFormat with a timeZone.
 *
 * @param ts - Epoch milliseconds
 * @returns Formatted time string, e.g. "14:23"
 */
function formatLocalTime(ts: number): string {
  const d = new Date(ts);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

/**
 * Drops the oldest resolved cards when the total count exceeds MAX_TOTAL_CARDS.
 * Resolved cards (turned-away, ambiguous) are preferred for removal over
 * active cards (detecting, entered).
 *
 * @param cards - Current card array
 * @returns Trimmed card array within the soft cap
 */
function trimCardCap(cards: StoryCardState[]): StoryCardState[] {
  if (cards.length <= MAX_TOTAL_CARDS) return cards;
  // Sort: keep detecting + entered at front; trim oldest resolved from the tail
  const active = cards.filter((c) => c.state === 'detecting' || c.state === 'entered');
  const resolved = cards
    .filter((c) => c.state === 'turned-away' || c.state === 'ambiguous')
    .sort((a, b) => (a.resolvedAt ?? 0) - (b.resolvedAt ?? 0));
  const keep = [...active, ...resolved].slice(0, MAX_TOTAL_CARDS);
  return keep;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * useStoryCards: manages the WebSocket-driven story card stack.
 *
 * @param _siteId - Site UUID (reserved for Phase 5 site-scoped subscriptions)
 * @returns cards, dismiss callback, and turnedAwayCount signal for the deterrence counter
 */
export function useStoryCards(_siteId: string | undefined): {
  cards: StoryCardState[];
  dismiss: (alertId: number) => void;
  turnedAwayCount: number;
} {
  const { onMessage, subscribe, unsubscribe } = useWebSocket();
  const [cards, setCards] = useState<StoryCardState[]>([]);
  const [turnedAwayCount, setTurnedAwayCount] = useState(0);

  // Subscribe to the alerts topic on mount; clean up on unmount.
  useEffect(() => {
    subscribe('alerts');
    return () => {
      unsubscribe('alerts');
    };
  // subscribe/unsubscribe are stable refs from context; safe to omit from deps
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Message handler: creates or resolves cards based on alert frames.
  useEffect(() => {
    const unregister = onMessage((frame) => {
      if (frame.type !== 'alert') return;
      const p = frame.payload;

      setCards((prev) => {
        const now = Date.now();

        // Look for an existing detecting card for this fingerprint within the match window.
        const existing =
          p.fingerprint != null
            ? prev.find(
                (c) =>
                  c.fingerprint === p.fingerprint &&
                  c.state === 'detecting' &&
                  now - c.openedAt < RESOLUTION_MATCH_WINDOW_MS,
              )
            : undefined;

        // Turnaround: resolve the existing card to 'turned-away'.
        if (existing && p.alert_type === 'turnaround') {
          const durationSec = Math.round((now - existing.openedAt) / 1000);
          setTurnedAwayCount((n) => n + 1);
          return prev.map((c) =>
            c.alertId === existing.alertId
              ? {
                  ...c,
                  state: 'turned-away' as const,
                  resolvedAt: now,
                  resolutionText: `Turned away · ${durationSec}s`,
                }
              : c,
          );
        }

        // Entry: resolve the existing card (or open a new one) to 'entered'.
        if (p.alert_type === 'entry') {
          if (existing) {
            return prev.map((c) =>
              c.alertId === existing.alertId
                ? {
                    ...c,
                    state: 'entered' as const,
                    resolvedAt: now,
                    resolutionText: `Entered at ${formatLocalTime(now)}`,
                  }
                : c,
            );
          }
          // No existing card - open a new entered card directly.
          const newCard: StoryCardState = {
            alertId: p.alert_id,
            openedAt: now,
            fingerprint: p.fingerprint,
            zoneName: p.zone_name,
            severity: p.severity,
            alertType: p.alert_type,
            cameraUrl: p.evidence_url,
            state: 'entered',
            resolvedAt: now,
            resolutionText: `Entered at ${formatLocalTime(now)}`,
          };
          return trimCardCap([newCard, ...prev]);
        }

        // Loiter resolved: resolve the existing card to 'ambiguous'.
        if (existing && p.alert_type === 'loiter_resolved') {
          return prev.map((c) =>
            c.alertId === existing.alertId
              ? {
                  ...c,
                  state: 'ambiguous' as const,
                  resolvedAt: now,
                  resolutionText: 'Resolved, device departed without entering',
                }
              : c,
          );
        }

        // Approach: open a new detecting card.
        if (p.alert_type === 'approach') {
          const newCard: StoryCardState = {
            alertId: p.alert_id,
            openedAt: now,
            fingerprint: p.fingerprint,
            zoneName: p.zone_name,
            severity: p.severity,
            alertType: p.alert_type,
            cameraUrl: p.evidence_url,
            state: 'detecting',
            resolvedAt: null,
            resolutionText: null,
          };
          return trimCardCap([newCard, ...prev]);
        }

        // Ambiguous loiter (no matching card, alert_type='loiter_resolved' or 'other') - ignore.
        return prev;
      });
    });

    return unregister;
  // onMessage is a stable ref from context
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-dismiss loop: remove turned-away and ambiguous cards 30s after resolution.
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setCards((prev) =>
        prev.filter(
          (c) =>
            !(
              (c.state === 'turned-away' || c.state === 'ambiguous') &&
              c.resolvedAt !== null &&
              now - c.resolvedAt > AUTO_DISMISS_DELAY_MS
            ),
        ),
      );
    }, 1_000);
    return () => clearInterval(interval);
  }, []);

  // Manual dismiss: removes a card by alertId regardless of state.
  const dismiss = useCallback((alertId: number) => {
    setCards((prev) => prev.filter((c) => c.alertId !== alertId));
  }, []);

  return { cards, dismiss, turnedAwayCount };
}
